import { supabase } from '../lib/supabase'
import { attributeLead } from './leadsService'
import {
  applyFilters,
  applyOrdering,
  applyRange,
  applyRoleConstraints,
  applySearch,
  applyOwnershipToPayload,
  handleQuery,
  formatSupabaseError,
  accessDeniedResponse,
  resolveRoleContext,
  ensureCanManageEvent
} from './utils'

const TABLE_NAME = 'event_registrations'

const RPC_REGISTER_FOR_EVENT = 'register_for_event_with_ref'
const RPC_CAPTURE_EARLY_LEAD = 'capture_early_lead'
const RPC_COMPLETE_REGISTRATION_FROM_LEAD = 'complete_registration_from_lead'

/**
 * Build registration URL with agent referral code for sharing.
 * Use when student clicks to register for recommended/public events so the agent gets credit.
 * @param {string} eventId
 * @param {string} [refCode] - agent_referral_code from users table
 * @param {string} [baseUrl] - e.g. window.location.origin
 * @returns {string}
 */
export const buildRegistrationUrlWithRef = (eventId, refCode, baseUrl = '') => {
  const base = baseUrl || (typeof window !== 'undefined' ? window.location?.origin : '')
  const path = `/register/${eventId}`
  if (refCode) return `${base}${path}?ref=${encodeURIComponent(refCode)}`
  return `${base}${path}`
}

/**
 * Anonymous registration via RPC (SECURITY DEFINER). Use this for the public registration form
 * so that unauthenticated visitors can register without hitting RLS on event_registrations.
 * Resolves agent from sharing link slug, attributes lead, and inserts the registration server-side.
 *
 * @param {string} eventId - UUID of the event
 * @param {string} [sharingLinkSlug] - Optional ref/slug from the sharing link URL
 * @param {{
 *   full_name: string,
 *   email: string,
 *   phone?: string,
 *   invited_by?: string
 * }} payload - Form data (full_name, email, phone, invited_by)
 * @param {{
 *   studentId?: string,
 *   leadId?: string,
 *   paymentIntentId?: string,
 *   paymentAmount?: number,
 *   status?: string,
 *   paymentStatus?: string
 * }} [options]
 * @returns {Promise<{ data: object | null, error: object | null }>} - data is the inserted registration row (JSONB)
 */
export const registerForEventWithRef = async (eventId, sharingLinkSlug, payload, options = {}) => {
  const {
    studentId = null,
    leadId = null,
    paymentIntentId = null,
    paymentAmount = null,
    status = 'registered',
    paymentStatus = 'completed'
  } = options

  const pPayload = {
    full_name: payload?.full_name ?? payload?.name ?? '',
    email: payload?.email ?? '',
    phone: payload?.phone ?? null,
    invited_by: payload?.invited_by ?? null
  }

  const { data, error } = await supabase.rpc(RPC_REGISTER_FOR_EVENT, {
    p_event_id: eventId,
    p_sharing_link_slug: sharingLinkSlug || null,
    p_payload: pPayload,
    p_student_id: studentId || null,
    p_lead_id: leadId || null,
    p_payment_intent_id: paymentIntentId || null,
    p_payment_amount: paymentAmount ?? null,
    p_status: status,
    p_payment_status: paymentStatus
  })

  if (error) {
    return {
      data: null,
      error: formatSupabaseError(error, 'Unable to complete registration.')
    }
  }

  return { data: data ?? null, error: null }
}

/**
 * Capture lead at Step 1 (name, email, phone) before sign-up. Callable anonymously.
 * Resolves agent from ref code; creates/updates lead with status 'pending'.
 * When the user completes Step 2/3, pass the returned leadId to registerForEventWithRef
 * so the same lead is updated to 'converted'.
 *
 * @param {string} eventId - UUID of the event
 * @param {{ email: string, full_name?: string, phone?: string }} payload - Step 1 form data
 * @param {string} [refCode] - Optional ref/slug from the sharing link URL
 * @returns {Promise<{ data: { ok: boolean, lead_id?: string, error?: string } | null, error: object | null }>}
 */
export const captureEarlyLead = async (eventId, payload, refCode = null) => {
  const { data, error } = await supabase.rpc(RPC_CAPTURE_EARLY_LEAD, {
    v_event_id: eventId,
    v_email: payload?.email ?? '',
    v_full_name: payload?.full_name ?? payload?.name ?? '',
    v_phone: payload?.phone ?? null,
    v_ref_code: refCode || null
  })

  if (error) {
    return {
      data: null,
      error: formatSupabaseError(error, 'Unable to capture lead.')
    }
  }

  if (data && typeof data === 'object' && data.ok === false && data.error) {
    return {
      data: null,
      error: { message: data.error }
    }
  }

  return {
    data: data ?? null,
    error: null
  }
}

/**
 * Validate lead + event + email and return lead/event data for resuming registration (Steps 2-3).
 * Used by /complete-registration/:eventId with query params leadId and email.
 *
 * @param {string} leadId - UUID of the lead
 * @param {string} eventId - UUID of the event
 * @param {string} email - Lead email (must match)
 * @returns {Promise<{ data: { lead: object, event: object } | null, error: object | null }>}
 */
export const completeRegistrationFromLead = async (leadId, eventId, email) => {
  if (!leadId || !eventId || !email?.trim()) {
    return {
      data: null,
      error: { message: 'Lead ID, event ID, and email are required.', code: 'invalid_input' }
    }
  }

  const { data, error } = await supabase.rpc(RPC_COMPLETE_REGISTRATION_FROM_LEAD, {
    p_lead_id: leadId,
    p_event_id: eventId,
    p_email: email.trim()
  })

  if (error) {
    return {
      data: null,
      error: formatSupabaseError(error, 'Unable to load registration details.')
    }
  }

  if (data && typeof data === 'object' && data.ok === false && data.error) {
    return {
      data: null,
      error: { message: data.error }
    }
  }

  if (!data?.lead || !data?.event) {
    return {
      data: null,
      error: { message: 'Invalid response from server.', code: 'invalid_response' }
    }
  }

  return {
    data: {
      lead: data.lead,
      event: data.event
    },
    error: null
  }
}

/**
 * Fetch registration counts by event IDs (for merging into events list).
 * Uses same RLS/role constraints; returns array of { event_id } for the caller to reduce to counts.
 * @param {string[]} eventIds
 * @param {{ role?: string, userId?: string }} [options]
 * @returns {Promise<{ data: Array<{ event_id: string }> | null, error: object | null }>}
 */
export const getRegistrationCountsForEventIds = async (eventIds, options = {}) => {
  if (!Array.isArray(eventIds) || eventIds.length === 0) {
    return { data: [], error: null }
  }

  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner') {
    return accessDeniedResponse('Partners can only access registrations for assigned events.')
  }

  let query = supabase.from(TABLE_NAME).select('event_id').in('event_id', eventIds)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'primary_agent_id',
    studentField: 'student_id',
    eventIds
  })

  const result = await handleQuery(query, { fallbackMessage: 'Unable to load registration counts.' })
  return result
}

/**
 * Fetch registrations with optional filtering and role constraints.
 * @param {{
 *   role?: 'student' | 'agent' | 'partner' | 'admin',
 *   userId?: string,
 *   eventIds?: string[],
 *   filters?: Array<{ column: string, operator: string, value: any }>,
 *   search?: string,
 *   searchColumns?: string[],
 *   orderBy?: { column: string, ascending?: boolean },
 *   range?: { from: number, to: number }
 * }} [options]
 */
export const listRegistrations = async (options = {}) => {
  const {
    role,
    userId,
    eventIds,
    filters,
    search,
    searchColumns = ['student_email', 'student_name'],
    orderBy = { column: 'created_at', ascending: false },
    range
  } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && (!eventIds || eventIds.length === 0)) {
    return accessDeniedResponse('Partners can only access registrations for assigned events.')
  }

  // Join users (when student_id set), leads (when lead_id set), and agent_sharing_links (when sharing_link_id set) for invitation link slug
  const selectColumns = `
    id, event_id, student_id, lead_id, student_name, student_email, student_phone, status, payment_status, created_at, sharing_link_id,
    attended, attendance_verified_at, invited_by,
    student:users!student_id(id, email, full_name, phone),
    lead:leads!lead_id(id, email, full_name, phone),
    agent_sharing_links!sharing_link_id(slug)
  `
  let query = supabase.from(TABLE_NAME).select(selectColumns)

  // Always scope to event(s) when eventIds provided so the Registrations tab shows only this event's data
  if (Array.isArray(eventIds) && eventIds.length > 0) {
    query = query.in('event_id', eventIds)
  }

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'primary_agent_id',
    studentField: 'student_id',
    eventIds
  })

  query = applyFilters(query, filters)
  query = applySearch(query, search, searchColumns)
  query = applyOrdering(query, orderBy)
  query = applyRange(query, range)

  const result = await handleQuery(query, { fallbackMessage: 'Unable to load registrations.' })
  if (result.error) {
    console.error('Registrations fetch error:', result.error)
  }
  return result
}

/**
 * Fetch a single registration by id.
 * @param {string} registrationId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventIds?: string[] }} [options]
 */
export const getRegistrationById = async (registrationId, options = {}) => {
  const { role, userId, eventIds } = options

  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && (!eventIds || eventIds.length === 0)) {
    return accessDeniedResponse('Partners can only access registrations for assigned events.')
  }

  let query = supabase.from(TABLE_NAME).select('*').eq('id', registrationId).maybeSingle()

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'primary_agent_id',
    studentField: 'student_id',
    eventIds
  })

  return handleQuery(query, {
    fallbackMessage: 'Unable to load the registration.',
    allowEmpty: false
  })
}

/**
 * Create a new registration.
 * @param {Record<string, any>} payload
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string }} [options]
 */
export const createRegistration = async (payload, options = {}) => {
  const { role, userId } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner' && !payload?.event_id) {
    return accessDeniedResponse('Partners can only register participants for assigned events.')
  }

  if ((resolvedRole === 'agent' || resolvedRole === 'partner') && payload?.event_id) {
    const access = await ensureCanManageEvent(payload.event_id)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  const normalizedPayload = {
    ...payload,
    invited_by: payload?.invitedBy ?? payload?.invited_by,
    invited_by_agent_id:
      payload?.invited_by_agent_id ?? payload?.invitedByAgentId ?? payload?.agentId ?? null,
    primary_agent_id:
      payload?.primary_agent_id ?? payload?.primaryAgentId ?? payload?.agentId ?? null
  }

  if ('invitedBy' in normalizedPayload) {
    delete normalizedPayload.invitedBy
  }
  if ('invitedByAgentId' in normalizedPayload) {
    delete normalizedPayload.invitedByAgentId
  }
  if ('primaryAgentId' in normalizedPayload) {
    delete normalizedPayload.primaryAgentId
  }
  if ('agentId' in normalizedPayload) {
    delete normalizedPayload.agentId
  }

  const data = applyOwnershipToPayload(normalizedPayload, {
    role: resolvedRole,
    userId: resolvedUserId,
    studentField: 'student_id',
    agentField: 'primary_agent_id'
  })

  const query = supabase.from(TABLE_NAME).insert(data).select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to create the registration.',
    allowEmpty: false
  })
}

/**
 * Update a registration.
 * @param {string} registrationId
 * @param {Record<string, any>} updates
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const updateRegistration = async (registrationId, updates, options = {}) => {
  const { role, userId, eventId, eventIds } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner') {
    const scopedEventId = eventId || updates?.event_id
    if (!scopedEventId && (!eventIds || eventIds.length === 0)) {
      return accessDeniedResponse('Partners can only update registrations for assigned events.')
    }
  }

  const scopedEventId = eventId || updates?.event_id
  if ((resolvedRole === 'agent' || resolvedRole === 'partner') && scopedEventId) {
    const access = await ensureCanManageEvent(scopedEventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  const normalizedUpdates = {
    ...updates,
    invited_by: updates?.invitedBy ?? updates?.invited_by
  }

  if ('invitedBy' in normalizedUpdates) {
    delete normalizedUpdates.invitedBy
  }

  let query = supabase.from(TABLE_NAME).update(normalizedUpdates).eq('id', registrationId)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'primary_agent_id',
    studentField: 'student_id',
    eventIds
  })

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to update the registration.',
    allowEmpty: false
  })
}

/**
 * Delete a registration.
 * @param {string} registrationId
 * @param {{ role?: 'student' | 'agent' | 'partner' | 'admin', userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const deleteRegistration = async (registrationId, options = {}) => {
  const { role, userId, eventId, eventIds } = options
  const roleContext = await resolveRoleContext({ role, userId })
  if (roleContext.error) {
    return { data: null, error: roleContext.error }
  }

  const resolvedRole = roleContext.role
  const resolvedUserId = roleContext.userId

  if (resolvedRole === 'partner') {
    if (!eventId && (!eventIds || eventIds.length === 0)) {
      return accessDeniedResponse('Partners can only delete registrations for assigned events.')
    }
  }

  if ((resolvedRole === 'agent' || resolvedRole === 'partner') && eventId) {
    const access = await ensureCanManageEvent(eventId)
    if (!access.allowed) {
      return { data: null, error: access.error }
    }
  }

  let query = supabase.from(TABLE_NAME).delete().eq('id', registrationId)

  query = applyRoleConstraints(query, {
    role: resolvedRole,
    userId: resolvedUserId,
    agentField: 'primary_agent_id',
    studentField: 'student_id',
    eventIds
  })

  query = query.select('*').single()

  return handleQuery(query, {
    fallbackMessage: 'Unable to delete the registration.',
    allowEmpty: false
  })
}

/**
 * Mark a registration as checked in (day-of attendance).
 * @param {string} registrationId
 * @param {{ role?: string, userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const checkInRegistration = async (registrationId, options = {}) => {
  const verifiedAt = new Date().toISOString()
  return updateRegistration(
    registrationId,
    { attended: true, attendance_verified_at: verifiedAt },
    options
  )
}

/**
 * Undo check-in for a registration.
 * @param {string} registrationId
 * @param {{ role?: string, userId?: string, eventId?: string, eventIds?: string[] }} [options]
 */
export const undoCheckIn = async (registrationId, options = {}) => {
  return updateRegistration(
    registrationId,
    { attended: false, attendance_verified_at: null },
    options
  )
}

/**
 * Walk-in flow: attribute lead, create registration with lead_id, then check in.
 * @param {{
 *   eventId: string,
 *   agentId?: string | null,
 *   fullName: string,
 *   email: string,
 *   phone?: string | null,
 *   invitedBy?: string | null
 * }} payload
 * @param {{ role?: string, userId?: string, eventIds?: string[] }} [options]
 */
export const registerWalkIn = async (payload, options = {}) => {
  const {
    eventId,
    agentId = null,
    fullName,
    email,
    phone = null,
    invitedBy = null
  } = payload

  if (!eventId) {
    return {
      data: null,
      error: { message: 'Event ID is required.', code: 'invalid_input' }
    }
  }

  const trimmedEmail = (email ?? '').trim()
  const trimmedName = (fullName ?? '').trim()
  if (!trimmedEmail || !trimmedName) {
    return {
      data: null,
      error: { message: 'Full name and email are required.', code: 'invalid_input' }
    }
  }

  const invitedByValue = invitedBy?.trim() || null
  const phoneValue = phone?.trim() || null

  const { data: leadId, error: leadError } = await attributeLead({
    email: trimmedEmail,
    full_name: trimmedName,
    phone: phoneValue,
    invited_by: invitedByValue,
    event_id: eventId,
    agent_id: agentId,
    source: 'walk_in'
  })

  if (leadError) {
    return { data: null, error: leadError }
  }

  const { data: registration, error: createError } = await createRegistration(
    {
      event_id: eventId,
      lead_id: leadId,
      student_name: trimmedName,
      student_email: trimmedEmail,
      student_phone: phoneValue,
      invited_by: invitedByValue,
      primary_agent_id: agentId,
      invited_by_agent_id: agentId,
      status: 'registered',
      payment_status: 'completed'
    },
    options
  )

  if (createError) {
    return { data: null, error: createError }
  }

  if (!registration?.id) {
    return {
      data: null,
      error: { message: 'Registration was not created.', code: 'invalid_response' }
    }
  }

  const checkInOptions = {
    ...options,
    eventId,
    eventIds: options.eventIds ?? (eventId ? [eventId] : undefined)
  }

  const { data: checkedIn, error: checkInError } = await checkInRegistration(
    registration.id,
    checkInOptions
  )

  if (checkInError) {
    return { data: registration, error: checkInError }
  }

  return { data: checkedIn ?? registration, error: null }
}
